import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
    Calculator,
    MessagesSquare,
    FileText,
    ClipboardList,
    MonitorPlay,
    Check,
    MessageCircle,
    ScanSearch,
    ReceiptText,
    Wrench,
    GraduationCap,
    ChevronRight,
    MapPin,
    Globe,
} from 'lucide-react';
import InquiryModal from '../../components/features/InquiryModal';
import VisitMap, { OUTSIDE_REGIONS, OVERSEAS } from '../../components/home/VisitMap';
import { MissionSection } from '../../components/home/MissionValues';
import ClosingCta from '../../components/home/ClosingCta';
import StatsBand, { STAT_ITEMS } from '../../components/home/StatsBand';

/*
 * 메인 — 2026-10 문구 개편.
 *
 * 나누는 기준은 **고객사의 규모가 아니라 프로젝트의 크기**다. 고객에게 ESL 은 본업을
 * 돕는 보조 수단이라, 반도체 대기업도 작은 프로젝트로 시작하고 동네 매장도 같은 장비를
 * 쓴다. 솔루션이 작은 것이지 쓰는 기업이 작은 것이 아니다.
 *
 * '중소비즈니스를 위한' 은 창업 목적이자 미션으로 그대로다. 다만 고객에게는 쉬운 말과
 * 실제 현장으로 보여 준다 — "기술의 혜택, 규모에 상관없이 누려야 하니까." 가 그 번역이다.
 *
 * 이모지는 쓰지 않는다(확정 디자인 원칙). 아이콘은 lucide.
 *
 * 테두리 있는 버튼에는 `border-solid` 를 꼭 붙인다. 전역 `button { border: none }`
 * (src/index.css)이 선 스타일을 none 으로 만들어, `border` 만 주면 테두리가 안 보인다.
 * 전역 규칙을 지우면 사이트 전체 버튼 모양이 한꺼번에 바뀔 수 있어 여기서만 맞춘다.
 *
 * 행간은 글자 크기와 한 클래스로 묶는다(`md:text-6xl/normal`). Tailwind 의 `text-6xl`
 * 같은 크기 클래스는 행간까지 함께 정하는데(6xl 은 1), 반응형 접두사(md:)가 붙으면
 * CSS 에서 뒤에 나와 따로 준 `leading-*` 를 덮어쓴다. 그래서 데스크톱에서만 두 줄이
 * 붙어 보였다 — 모바일은 멀쩡해서 원인을 찾기 어려웠다.
 */

const PROMISES = ['도입 후 유지비 0원', '전담 매니저 한 명이 끝까지', '기술용어, 몰라도 됩니다'];

const CTAS = [
    { label: '3분 셀프 견적', icon: Calculator, href: '/estimate', primary: true },
    { label: '상담/미팅', icon: MessagesSquare, type: 'CONTACT' },
    { label: '제안서 받기', icon: FileText, type: 'PROPOSAL' },
    { label: '맞춤 견적 요청', icon: ClipboardList, type: 'ESTIMATE' },
    { label: '데모 신청', icon: MonitorPlay, type: 'DEMO' },
];


const STEPS = [
    { label: '상담', icon: MessageCircle },
    { label: '현장 확인 · 데모 시연', icon: ScanSearch },
    { label: '견적 · 제안', icon: ReceiptText },
    { label: '세팅 · 설치', icon: Wrench },
    { label: '사용자 교육 · 사후관리', icon: GraduationCap },
];

const Home = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const [modalData, setModalData] = useState({ isOpen: false, type: 'ESTIMATE' });

    const openModal = (type) => setModalData({ isOpen: true, type });

    /*
     * 다른 페이지에서 헤더의 '가치' 를 누르면 여기로 오면서 가야 할 섹션을 state 로 넘긴다.
     * 새 탭으로 `/#mission` 을 연 경우는 hash 로 온다. SPA 는 브라우저의 앵커 이동이
     * 화면이 그려지기 전에 끝나 버려서, 그려진 뒤에 직접 옮겨 준다.
     */
    useEffect(() => {
        const target = location.state?.scrollTo || (location.hash ? location.hash.slice(1) : null);
        if (!target) return undefined;
        const t = setTimeout(() => {
            document.getElementById(target)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
            if (location.state?.scrollTo) navigate('/', { replace: true, state: null });
        }, 60);
        return () => clearTimeout(t);
    }, [location.state, location.hash, navigate]);

    return (
        <div className="bg-surface-bg min-h-screen pb-24">
            <InquiryModal
                isOpen={modalData.isOpen}
                onClose={() => setModalData((prev) => ({ ...prev, isOpen: false }))}
                type={modalData.type}
            />

            {/* ── 1. 메인 ─────────────────────────────────────────────── */}
            <section id="hero" className="pt-36 md:pt-44 pb-20 px-6 text-center">
                <div className="max-w-4xl mx-auto">
                    <h1 className="text-4xl/normal sm:text-5xl/normal md:text-6xl/normal font-extrabold text-gray-900 tracking-tight break-keep">
                        1개든 10,000개든,<br />
                        끝까지 완벽하게.
                    </h1>

                    {/*
                      명칭은 현장마다 다르다 — 매장은 가격표, 사무실은 명패, 창고는 라벨.
                      셋을 다 적어야 각자 자기 이야기로 알아본다. 정식 명칭은 아래 각주에 모았다.
                    */}
                    <p className="mt-6 text-lg/relaxed md:text-2xl/relaxed text-gray-600 font-medium break-keep">
                        전자 가격표·전자 명패·전자 라벨 도입,<br className="hidden md:block" />{' '}
                        컨설팅부터 설치까지 쉽고 빠르게 맞춰 드립니다.
                    </p>

                    <ul className="mt-8 flex flex-col sm:flex-row flex-wrap justify-center items-center gap-x-7 gap-y-2.5 list-none p-0 m-0">
                        {PROMISES.map((p) => (
                            <li key={p} className="inline-flex items-center gap-2 text-base md:text-lg font-semibold text-gray-800">
                                <Check className="w-5 h-5 text-primary-500 shrink-0" strokeWidth={2.75} aria-hidden="true" />
                                {p}
                            </li>
                        ))}
                    </ul>

                    <div className="mt-12 flex flex-wrap justify-center gap-3 md:gap-4">
                        {CTAS.map(({ label, icon: Icon, href, type, primary }) => (
                            <button
                                key={label}
                                type="button"
                                onClick={() => (href ? navigate(href) : openModal(type))}
                                className={
                                    'flex flex-col items-center justify-center gap-2 w-[104px] h-[100px] md:w-[124px] md:h-[112px] rounded-2xl p-2 transition-all hover:-translate-y-1 '
                                    + (primary
                                        ? 'bg-primary-500 text-white shadow-soft hover:bg-primary-600'
                                        : 'bg-white text-gray-700 border border-solid border-gray-200 shadow-sm hover:border-primary-500 hover:text-primary-700 hover:bg-primary-50')
                                }
                            >
                                <Icon className="w-7 h-7 md:w-8 md:h-8" strokeWidth={1.75} aria-hidden="true" />
                                <span className="text-xs md:text-[13px] font-bold tracking-tight leading-tight whitespace-nowrap">{label}</span>
                            </button>
                        ))}
                    </div>

                    {/*
                      실적은 숫자 두 개로만 말한다. "반도체 대기업부터 동네 매장까지" 같은 수식보다
                      꾸밈 없는 숫자가 더 믿긴다. 할 일(버튼) 다음에 근거를 둔다.
                      숫자는 constants/stats.js 에서 고친다.
                    */}
                    <StatsBand
                        items={[STAT_ITEMS.projects, STAT_ITEMS.solutions]}
                        className="mt-14 pt-10 border-t border-gray-200"
                    />

                    {/*
                      같은 장비를 현장마다 다른 이름으로 부른다. 고객이 아는 이름으로 찾아올 수 있게
                      전부 적는다 — 검색엔진도 이 이름들로 이 페이지를 찾는다.
                    */}
                    <p className="mt-10 max-w-2xl mx-auto text-xs md:text-sm text-gray-500 leading-relaxed break-keep">
                        * 현장마다 부르는 이름이 다릅니다. 매장에서는 <strong className="font-semibold text-gray-600">전자가격표시기</strong>,
                        사무실·회의실에서는 <strong className="font-semibold text-gray-600">전자명패</strong>,
                        창고·재고 현장에서는 <strong className="font-semibold text-gray-600">전자표시기</strong>나{' '}
                        <strong className="font-semibold text-gray-600">스마트라벨</strong>이라고 부릅니다.
                        모두 같은 장비, <strong className="font-semibold text-gray-600">ESL</strong>입니다.
                    </p>
                </div>
            </section>

            {/* ── 2. 전국 방문 ────────────────────────────────────────── */}
            <section id="visit" className="scroll-mt-24 bg-white border-y border-gray-100 py-20 md:py-24 px-6">
                <div className="max-w-5xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-12 md:gap-8 items-center">
                    <div className="text-center md:text-left">
                        <h2 className="text-3xl/normal md:text-4xl/normal font-extrabold text-gray-900 tracking-tight break-keep">
                            전국 어디든,<br />
                            직접 찾아갑니다.
                        </h2>
                        <p className="mt-5 text-lg md:text-xl text-gray-600 font-medium break-keep">
                            방문 컨설팅 · 데모 · 장비 대여 <strong className="text-gray-900 font-extrabold">무료</strong>
                        </p>

                        {/*
                          숫자는 지도 데이터에서 센다. 손으로 적으면 지역을 더할 때마다 어긋난다
                          (원주가 더해지며 9 → 10 이 됐다).
                        */}
                        <p className="mt-10 flex flex-wrap items-baseline justify-center md:justify-start gap-x-2 gap-y-1">
                            <span className="text-base font-bold text-gray-500">서울·경기 밖</span>
                            <span className="text-6xl font-extrabold text-primary-500 leading-none">{OUTSIDE_REGIONS.length}</span>
                            <span className="text-xl font-bold text-gray-900">개 지역 방문·설치</span>
                        </p>
                        <p className="mt-3 inline-flex items-center gap-1.5 text-base font-bold text-gray-700">
                            <Globe className="w-4 h-4 text-primary-500" strokeWidth={2.25} aria-hidden="true" />
                            해외 {OVERSEAS.join('·')}까지
                        </p>

                        {/*
                          지도가 하려는 말은 "어디든 간다" 이니, 그 말 바로 뒤에서 미팅을 청할 수 있게 한다.
                          지역 때문에 망설이는 사람에게 필요한 다음 행동이 여기 있다.
                        */}
                        <div className="mt-10 pt-8 border-t border-gray-100">
                            <p className="text-base md:text-lg text-gray-700 font-medium break-keep">
                                지역 상관없이, 부담 없이 미팅을 요청하세요.
                            </p>
                            <button
                                type="button"
                                onClick={() => openModal('CONTACT')}
                                className="mt-4 inline-flex items-center gap-2 rounded-full bg-white border-2 border-solid border-primary-500 text-primary-700 hover:bg-primary-50 text-base font-extrabold px-6 py-3 transition-colors"
                            >
                                <MapPin className="w-4 h-4" strokeWidth={2.5} aria-hidden="true" />
                                무료 방문 상담 요청
                            </button>
                        </div>
                    </div>

                    <VisitMap />
                </div>
            </section>

            {/* ── 3. 도입 프로세스 ────────────────────────────────────── */}
            <section id="process" className="scroll-mt-24 py-20 md:py-24 px-6">
                <div className="max-w-6xl mx-auto">
                    <h2 className="text-3xl font-bold text-center text-gray-900 mb-14">도입 프로세스</h2>

                    <ol className="flex flex-col md:flex-row md:items-stretch list-none p-0 m-0">
                        {STEPS.map(({ label, icon: Icon }, i) => (
                            <React.Fragment key={label}>
                                <li className="flex-1 flex md:flex-col items-center md:text-center gap-4 md:gap-3 bg-white rounded-2xl border border-gray-200 px-5 py-4 md:px-4 md:py-7">
                                    <span className="w-12 h-12 rounded-full bg-primary-50 text-primary-700 flex items-center justify-center shrink-0">
                                        <Icon className="w-6 h-6" strokeWidth={1.75} aria-hidden="true" />
                                    </span>
                                    <span className="flex flex-col md:items-center">
                                        <span className="text-xs font-extrabold text-primary-700">{String(i + 1).padStart(2, '0')}</span>
                                        <span className="mt-0.5 font-bold text-gray-900 break-keep leading-snug">{label}</span>
                                    </span>
                                </li>
                                {i < STEPS.length - 1 && (
                                    <li aria-hidden="true" className="flex items-center justify-center py-1.5 md:py-0 md:px-1.5 text-gray-300">
                                        <ChevronRight className="w-5 h-5 rotate-90 md:rotate-0" />
                                    </li>
                                )}
                            </React.Fragment>
                        ))}
                    </ol>

                    <p className="mt-8 text-center text-sm text-gray-600 leading-relaxed break-keep">
                        * 네트워크 공사가 필요하면 고객사가 공사 업체에 바로 맡길 수 있는 준비 사양서를 드립니다.
                    </p>
                </div>
            </section>

            {/* ── 4. 미션과 핵심가치 ─────────────────────────────────── */}
            {/*
              회사소개(/about)에도 같은 섹션이 그대로 들어간다(MissionSection).
              id 는 예전 '가치' 메뉴 주소(/#mission)로 들어오는 링크를 위해 남긴다.
            */}
            <MissionSection id="mission" />

            {/* ── 5. 마무리 ───────────────────────────────────────────── */}
            <ClosingCta onRequest={() => openModal('CONTACT')} />
        </div>
    );
};

export default Home;
